// Central registry of ad-placement preview images per ad product.
// Used by both:
//   - /app/frontend/src/pages/AdProperties.js  (Ad Format Breakdown)
//   - /app/frontend/src/pages/FullFunnel.js    (Funnel Breakdown, deepest drill level)
//
// Key format:
//   'Ad Product Name'              -> unscoped fallback
//   '<Channel>:Ad Product Name'    -> channel-scoped preview (preferred)
//
// The `getAdPreview(channel, product)` helper tries the channel-scoped key
// first and falls back to the unscoped key. This lets the same product name
// (e.g. "Sponsored Products") show different placement mocks per channel
// (Amazon vs. Zepto).

export const AD_PREVIEWS = {  'PLA': {
    label: 'PLA - Product Listing Ads',
    placements: [
      { name: 'Search Slot', description: 'Sponsored product listings appear in search results', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/79zxoetj_PLA%20Search%20Slot.png' },
      { name: 'Product Page Slot', description: 'Ads shown in "Similar Products" section on product pages', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/s0svmjnc_PLA%20Product%20Page%20Slot.png' },
      { name: 'Product Page - You May Also Like', description: 'Ads shown in "You may also like" carousel', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/inv0gzab_PLA%20Product%20Page%202.png' },
    ]
  },
  'PCA': {
    label: 'PCA - Product Context Ads',
    placements: [
      { name: 'Search Slot', description: 'Brand banner displayed at top of search results', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/f391gvdp_PCA%20Search%20Slot.png' },
      { name: 'Product Page Slot', description: 'Brand banner displayed on product detail pages', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/9k9ocen2_PCA%20Product%20Page%20Slot.png' },
    ]
  },
  'Brand Ads': {
    label: 'Brand Ads',
    placements: [
      { name: 'Search Slot', description: 'Full-width brand creative banner on search pages', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/f391gvdp_PCA%20Search%20Slot.png' },
      { name: 'Product Page Slot', description: 'Brand creative banner on product pages', image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/9k9ocen2_PCA%20Product%20Page%20Slot.png' },
    ]
  },
  'Sponsored Products': {
    label: 'Sponsored Products',
    placements: [
      {
        name: 'Sponsored Product Listing Ad',
        description: 'Sponsored product appears in Amazon search results with a "Sponsored" label and the standard listing layout (image, title, rating, deal badge, price, prime).',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/06hazmbq_Sponsored%20Product%20Listing%20Ad.jpeg',
      },
    ],
  },
  // Zepto-scoped Sponsored Products preview. The lookup helper below tries
  // `${channel}:${product}` first, so Zepto users see the Zepto placements
  // while Amazon users continue to see the Amazon "Sponsored Product Listing
  // Ad" screenshot above.
  'Zepto:Sponsored Products': {
    label: 'Sponsored Products (Zepto)',
    placements: [
      {
        name: 'Listing Page',
        description: 'Sponsored product surfaces inside category / search-result listing pages on Zepto with a "Sponsored" pill above the product tile. Sits alongside organic results and inherits the standard product card layout.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/1whbwklb_Sponsored%20Product%20Ads%2C%20Listing%20Page%20%28Sponsored%20Products%29.jpeg',
      },
      {
        name: 'Cart Page — Swap & Save',
        description: 'Sponsored product appears inside the cart drawer\'s "Swap & Save" section, offering shoppers a lower-priced alternative right before checkout. High-intent conversion placement unique to Zepto.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/3ax175dw_Spondored%20Product%20Ads%2C%20Cart%20Page%20%28Swap%20%26%20Save%29.jpeg',
      },
    ],
  },
  'Zepto:Sponsored Display': {
    label: 'Sponsored Display (Zepto)',
    placements: [
      {
        name: 'Category Page — Banner Carousel',
        description: 'Full-width sponsored banner rendered at the top of the "All Categories" page, above the "Trending Categories" grid. Rotates through the banner carousel — highest-impression Sponsored Display placement on Zepto.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/fg89ljog_Sponsored%20Display%20Ads%2C%20Category%20Page%20%28Banner%20Carousel%29.jpeg',
      },
      {
        name: 'Home Page — Banner Carousel',
        description: 'Sponsored banner directly beneath the home-page search bar and category chips, above the "Flash Sale" strip. Prime real-estate for reach + branding on the app\'s landing surface.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/7yp4akco_Sponsored%20Display%20Ads%2C%20Home%20Page%20%28Banner%20Carousel%29.jpeg',
      },
      {
        name: 'Home Page — Brands in Focus',
        description: 'Sponsored brand tile inside the "Brands in focus" row on the home page. Three-tile carousel — ideal for brand-lift campaigns targeting shoppers browsing top-of-funnel.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/x822m3jx_Sponsored%20Display%20Ads%2C%20Home%20Page%20%28Brands%20in%20Focus%29.jpeg',
      },
      {
        name: 'Home Page — Featured Brands',
        description: 'Sponsored brand tile inside the "Featured Brands" row on the home page ("Curated especially for you"). Similar three-tile format to Brands in Focus but sits deeper in the home feed.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/a0k7r4u2_Sponsored%20Display%20Ads%2C%20Home%20Page%20%28Featured%20Brands%29.jpeg',
      },
      {
        name: 'Search — Search Suggestion',
        description: 'Native ad card injected inside the search-suggestions dropdown (Ad headline + sub-headline + Shop now CTA). Fires on high-intent search input — great for driving conversion on branded keyword queries.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/umw56rzv_Sponsored%20Display%20Ads%2C%20Search%20%28Search%20Suggestions%29.jpeg',
      },
    ],
  },
  'Zepto:Sponsored Brand': {
    label: 'Sponsored Brand (Zepto)',
    placements: [
      {
        name: 'Search Page — Product Collection Ad (PCA)',
        description: 'Branded product-collection card injected within the search-results feed on Zepto. Features the brand logo, headline strip and a multi-product carousel — designed to capture high-intent search traffic with a curated catalogue view.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/z9ov0psr_Sponsored%20Brand%20Ads%2C%20Search%20Page%20%28PCA%29.jpeg',
      },
      {
        name: 'Pre-Search — Spotlight',
        description: 'Full-bleed Spotlight creative surfaced on the pre-search screen (before a user types a query). Prime awareness placement combining brand banner + hero product — ideal for launches and top-of-funnel branding.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/67n92cbj_Sponsored%20Brand%20Ads%2C%20Pre%20Search%20%28Spotlight%29.jpeg',
      },
      {
        name: 'Pre-Search — Product Collection Ad (PCA)',
        description: 'Branded product-collection card on the pre-search screen alongside "Popular searches" and category chips. Shows the brand banner plus a horizontal product carousel — drives discovery before shoppers formulate a query.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/qed3svqr_Sponsored%20Brand%20Ads%2C%20Pre%20Search%20%28PCA%29.jpeg',
      },
    ],
  },
  'Blinkit:Product Booster': {
    label: 'Product Booster (Blinkit)',
    placements: [
      {
        name: 'Search & Category Listing — Sponsored Product',
        description: 'Boosts your product\'s visibility on Blinkit search and category listing pages. Ad-tagged product tile surfaces near the top of the listing grid, driving discovery for high-intent shoppers browsing the storefront.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/22qkaudd_Product%20Booster.png',
      },
    ],
  },
  'Blinkit:Recommendation Ads': {
    label: 'Recommendation Ads (Blinkit)',
    placements: [
      {
        name: 'Recommended for You — Sponsored Tile',
        description: 'Places your product inside Blinkit\'s "Recommended for you" recommendation engine surfaces. Ad-tagged tile sits alongside personalised organic recommendations — ideal for cross-sell / upsell targeting.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/fuof0cjm_Recommendation%20Ads.png',
      },
    ],
  },
  'Blinkit:Product Shelf': {
    label: 'Product Shelf (Blinkit)',
    placements: [
      {
        name: 'High-Visibility Shelf Slot',
        description: 'Showcases your products in a dedicated high-visibility shelf on the Blinkit home / category feed (e.g. "Original coffee with great taste"). Full-shelf branded strip driving discovery and engagement.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/15p3en97_Product%20Shelf.png',
      },
    ],
  },
  'Blinkit:Brand Booster': {
    label: 'Brand Booster (Blinkit)',
    placements: [
      {
        name: 'Search & Category — Shop by Brands',
        description: 'Enhances brand visibility inside Blinkit search and category listings via the "Shop by brands" carousel. Branded logo tile drives shopper attention to the brand storefront during high-intent browsing.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/1wy13sg6_Brand%20Booster.png',
      },
    ],
  },
  'Blinkit:Brand Spotlight': {
    label: 'Brand Spotlight (Blinkit)',
    placements: [
      {
        name: 'Home Feed — Shop by Brands (Sponsored)',
        description: 'Enhances brand visibility and builds consideration through sponsored brand tiles surfaced across multiple feeds (home "Shop by brands", category feeds). Three-tile carousel format with brand logos + hero product.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/2dl75es6_Brand%20Spotlight.png',
      },
    ],
  },
  'Blinkit:Listing Spotlight': {
    label: 'Listing Spotlight (Blinkit)',
    placements: [
      {
        name: 'Search Listing — Spotlight Card',
        description: 'Full-height branded spotlight card injected into the search listing grid ("Your favourite coffee for all seasons" with Shop now CTA). Enhances brand visibility and drives new-customer acquisition on high-intent search pages.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/jqnd48hr_Listing%20Spotlight.png',
      },
    ],
  },
  'Blinkit:Prime Banner': {
    label: 'Prime Banner (Blinkit)',
    placements: [
      {
        name: 'Feed & Category — High-Impact Banner',
        description: 'High-impact banner placements on the Blinkit home feed and category pages ("Your partner in perfect cooking" hero banner). Prime real-estate for reach + branding with strong visual footprint.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/5xvx2tju_Prime%20Banner.png',
      },
    ],
  },
  'Blinkit:Stories': {
    label: 'Stories (Blinkit)',
    placements: [
      {
        name: 'Top of Feed — Story Tile',
        description: 'Brings your products and new launches to the top of the Blinkit feed inside a swipeable Story tile format. Vertical creative surfaces above the "Recommended for you" strip — ideal for launches, offers and time-sensitive campaigns.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/45pl8kkv_Stories.png',
      },
    ],
  },
  'Sponsored Brand': {
    label: 'Sponsored Brand',
    placements: [
      {
        name: 'Sponsored Brands Banner',
        description: 'Branded headline placement at the top of search results — brand logo on the left, sponsored copy + best-seller badge, plus a multi-product banner showcasing the catalogue.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/3e9zok58_Sponsored%20Brands%20Banner.jpeg',
      },
      {
        name: 'Sponsored Brands Video',
        description: 'Top-of-search video creative with auto-play, mute toggle and the featured product card directly below. High-impact awareness placement.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/hj8mft2h_Sponsored%20Brands%20Video.jpeg',
      },
    ],
  },
  'Sponsored Display': {
    label: 'Sponsored Display',
    placements: [
      {
        name: 'Sponsored Display Banner',
        description: 'Contextual ad surfaced on product detail pages and similar high-intent surfaces (e.g. "Need help finding the right supplement?"). Drives consideration outside search.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/nam35bu6_Sponsored%20Display%20Banner.jpeg',
      },
    ],
  },
  'Telegram Ads': {
    label: 'Telegram Ads',
    placements: [
      {
        name: 'Telegram Ad',
        description: 'Sponsored deal post inside a high-subscriber Telegram offers channel — product card with discounted price, MRP strike-through, percentage off, deeplink and reactions feed.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/enelw8q1_Telegram%20Ad.jpeg',
      },
    ],
  },
  'Whatsapp Ads': {
    label: 'Whatsapp Ads',
    placements: [
      {
        name: 'Whatsapp Ad — Flipkart',
        description: 'Flipkart business-account broadcast on WhatsApp — rich media creative with offer banner, link preview card and a "Shop now" CTA driving traffic to flipkart.com.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/6wh2s669_Whatsapp%20Ad%20Flipkart.jpeg',
      },
    ],
  },
  'Meta Ads by FK': {
    label: 'Meta Ads by FK',
    placements: [
      {
        name: 'Meta by FK Preview',
        description: 'Facebook / Instagram in-feed sponsored carousel served via Flipkart\'s Meta ads buy — verified brand handle, social engagement strip (like / comment / share / save) and a "Shop now" CTA that deeplinks back to Flipkart.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/7axu1wu6_Meta%20by%20FK%20Preview.png',
      },
    ],
  },
  'Criteo by FK': {
    label: 'Criteo by FK',
    placements: [
      {
        name: 'Criteo by FK Preview',
        description: 'Criteo display network banner served through Flipkart on third-party publisher inventory (e.g. cricbuzz). Sale callout, hero product, price and a Flipkart-branded "Sale starts" badge.',
        image: 'https://customer-assets.emergentagent.com/job_trace-performance/artifacts/w5xblgvf_Criteo%20by%20FK%20%20Preview.png',
      },
    ],
  },
  '3P DSP': { label: '3P DSP', placements: [] },
  // ────────────────────────────────────────────────────────────────
  // Blinkit ad products — Performance (Product Booster / Recommendation Ads /
  // Product Shelf) and Reach (Stories / Prime Banner / Brand Spotlight /
  // Listing Spotlight / Brand Booster). Placement previews intentionally
  // empty for now — the user will share creative samples later, at which
  // point we'll wire image + description per placement (mirroring how
  // Amazon SP / Flipkart PCA cards work).
  'Product Booster':    { label: 'Product Booster', placements: [] },
  'Recommendation Ads': { label: 'Recommendation Ads', placements: [] },
  'Product Shelf':      { label: 'Product Shelf', placements: [] },
  'Stories':            { label: 'Stories', placements: [] },
  'Prime Banner':       { label: 'Prime Banner', placements: [] },
  'Brand Spotlight':    { label: 'Brand Spotlight', placements: [] },
  'Listing Spotlight':  { label: 'Listing Spotlight', placements: [] },
  'Brand Booster':      { label: 'Brand Booster', placements: [] },
};

/**
 * Resolve an AD_PREVIEWS entry with channel-scoped precedence.
 *
 * Callers should pass both channel + product; we try `${channel}:${product}`
 * first and fall back to the unscoped product key so legacy entries keep
 * working.
 */
export const getAdPreview = (channel, product) => {
  if (!product) return null;
  if (channel && AD_PREVIEWS[`${channel}:${product}`]) return AD_PREVIEWS[`${channel}:${product}`];
  return AD_PREVIEWS[product] || null;
};

/**
 * Returns the resolved preview key (channel-scoped if available, else raw
 * product name), or null if neither exists. Handy for wiring click handlers.
 */
export const getAdPreviewKey = (channel, product) => {
  if (!product) return null;
  if (channel && AD_PREVIEWS[`${channel}:${product}`]) return `${channel}:${product}`;
  if (AD_PREVIEWS[product]) return product;
  return null;
};
